/**
 * useCrud — small reusable hook for admin CRUD panels.
 * Handles: load list, add, delete, busy state, optional edit.
 */
import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";

export function useCrud({ listEndpoint, createEndpoint, deleteEndpoint, transform }) {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(listEndpoint);
      setItems(transform ? transform(r.data) : r.data);
    } catch (e) {
      toast.error("Erreur de chargement");
    }
  }, [listEndpoint, transform]);

  useEffect(() => { load(); }, [load]);

  const create = async (payload, { onSuccess, successMsg = "Créé" } = {}) => {
    setBusy(true);
    try {
      const r = await api.post(createEndpoint, payload);
      toast.success(successMsg);
      await load();
      onSuccess?.(r.data);
      return r.data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
      return null;
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id, { confirm = "Supprimer cet élément ?" } = {}) => {
    if (confirm && !window.confirm(confirm)) return;
    try {
      const url = typeof deleteEndpoint === "function" ? deleteEndpoint(id) : `${deleteEndpoint}/${id}`;
      await api.delete(url);
      toast.success("Supprimé");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  return { items, busy, load, create, remove };
}
